// InfinitePracticeMini.jsx

import React, { Component } from 'react';
import noImage from '../../../tools/auto/no-image.jpeg';
import { inject, observer } from 'mobx-react';
import confetti from 'canvas-confetti';
import { RingLoader } from 'react-spinners';
import ApiStore from '../../../api/ApiStore';

@inject('store')
@observer
class InfinitePracticeMini extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      questionData: null,
      selectedAnswer: null,
      showResult: false,
      answeringEnabled: true,
      questionIds: [],
      currentQuestionIndex: 0,
      completed: false,
      totalCorrect: 0,
      userAnswers: [],
      videoId: props.videoId || null,
      streak: 0,
      showSnackbar: false,
      snackbarMessage: '',
    };

    // Initialize sound effects
    this.successSoundPaths = [
      '/sounds/success1.mp3',
      '/sounds/success2.mp3',
      '/sounds/success3.mp3',
      '/sounds/success4.mp3',
      '/sounds/success5.mp3',
      '/sounds/success6.mp3',
      '/sounds/success7.mp3',
      '/sounds/success8.mp3',
      '/sounds/success9.mp3',
      '/sounds/success10.mp3',
      '/sounds/success11.mp3',
      '/sounds/success12.mp3',
      '/sounds/success13.mp3',
      '/sounds/success14.mp3',
      '/sounds/success15.mp3',
      '/sounds/success16.mp3',
      '/sounds/success17.mp3',
      '/sounds/success18.mp3',
    ];

    this.successSounds = [];
    this.initializeSounds();

    this.successMessages = [
      '¡Nivel desbloqueado! 🚀 Has alcanzado nuevas alturas. ¡Tómate un momento para celebrar antes de seguir conquistando!',
      '¡Bravo! Eres un auténtico maestro del aprendizaje. 🦸‍♂️ Tu esfuerzo ha dado sus frutos. ¡Sigue así y conquista el próximo nivel!',
      '¿Escuchas eso? 🎶 Es el sonido de la victoria y de un nuevo nivel. ¡Que siga la fiesta!',
      '¡Uh, uh! Nivel desbloqueado como un ninja! 🥷 Nadie ha visto cómo lo hiciste... pero sabemos que lo hiciste genial.',
      '¡Mira tú! Eres como un Pokémon en evolución. 🐉 Has desbloqueado un nuevo nivel.',
      '¡Estás a un paso de la cima! ⛰️ Un nivel más ha caído ante tu sabiduría. ¿Listo para ascender más alto?',
      '¡Sí, lo hiciste! 🎯 Has superado otro desafío. ¡El próximo nivel te está esperando con los brazos abiertos!',
    ];
  }

  initializeSounds() {
    this.successSoundPaths.forEach(path => {
      const sound = new Audio(path);
      sound.load();
      sound.onerror = e => console.error(`Error loading sound ${path}:`, e);
      this.successSounds.push(sound);
    });
  }

  componentDidMount() {
    if (this.state.videoId) {
      this.fetchQuestionIdsForVideo(this.state.videoId);
    } else {
      this.setState({ isLoading: false, completed: true });
    }
  }

  fetchQuestionIdsForVideo = async videoId => {
    try {
      const { store } = this.props;
      const organizationName = this.props.store.profile.organization || 'Madel';
      const licenseType = 'auto';
      const alcancePreguntas =
        store.orgSettings?.configVideos?.alcancePreguntas;

      let questionIdsData;

      if (alcancePreguntas === 'actual') {
        // Fetch questions for the current user's level only
        try {
          // Get current user level first using the JWT-based endpoint
          const currentLevel = await ApiStore.getCurrentLevelAuto(
            licenseType,
            organizationName
          );

          // Then get question IDs for that video filtered by level
          questionIdsData = await ApiStore.getQuestionIdsForVideoLevel(
            videoId,
            currentLevel
          );
        } catch (levelError) {
          console.error(
            'Error fetching user level, falling back to all questions:',
            levelError
          );
          // Fallback to getting all question IDs for this video without level filtering
          questionIdsData = await ApiStore.getQuestionIdsForVideo(videoId);
        }
      } else {
        // Fetch all questions for this video regardless of level
        questionIdsData = await ApiStore.getQuestionIdsForVideo(videoId);
      }

      if (
        !questionIdsData.question_ids ||
        questionIdsData.question_ids.length === 0
      ) {
        this.setState({ isLoading: false, completed: true });
        return;
      }

      this.setState({ questionIds: questionIdsData.question_ids }, () => {
        // this.shuffleQuestionIds();
        this.loadNextQuestion();
      });
    } catch (error) {
      console.error('Error fetching question IDs for video:', error);
      this.setState({ isLoading: false, completed: true });
    }
  };

  shuffleQuestionIds = () => {
    const { questionIds } = this.state;
    for (let i = questionIds.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [questionIds[i], questionIds[j]] = [questionIds[j], questionIds[i]];
    }
    this.setState({
      questionIds,
      currentQuestionIndex: 0,
      completed: false,
      totalCorrect: 0,
      userAnswers: [],
      streak: 0,
    });
  };

  loadNextQuestion = async () => {
    const { questionIds, currentQuestionIndex, completed } = this.state;

    if (completed) {
      return;
    }

    if (currentQuestionIndex >= questionIds.length) {
      this.setState({ completed: true, isLoading: false });
      return;
    }

    this.setState({
      isLoading: true,
      nextQuestionAvailable: false,
      showResult: false,
      selectedAnswer: null,
      answeringEnabled: true,
    });

    try {
      const questionId = questionIds[currentQuestionIndex];
      const response = await ApiStore.getQuestionDetails(questionId);

      const {
        question_id,
        text,
        explanation,
        options,
        correct_answer,
        image_data,
      } = response;

      const answers = Object.entries(options)
        .filter(([key, value]) => value)
        .map(([key, value]) => ({
          text: value,
          isCorrect: key === correct_answer,
        }));

      const imageSrc = image_data
        ? `data:image/png;base64,${image_data}`
        : noImage;

      this.setState({
        questionData: {
          questionId: question_id,
          question: text,
          image: imageSrc,
          answers: answers,
          explanation: explanation,
        },
        isLoading: false,
      });
    } catch (error) {
      console.error('Error loading question:', error);
      this.setState({ isLoading: false, questionData: null });
    }
  };

  handleAnswerSelection = async answer => {
    if (!this.state.answeringEnabled) return;

    const userId = this.props.store.profile._id;
    const { questionData } = this.state;
    const organizationName = this.props.store.profile.organization;

    const isCorrect = answer.isCorrect;

    const userAnswer = {
      questionId: questionData.questionId,
      questionText: questionData.question,
      selectedAnswer: answer.text,
      isCorrect: isCorrect,
      correctAnswer: questionData.answers.find(ans => ans.isCorrect).text,
      explanation: questionData.explanation,
      image: questionData.image,
    };

    this.setState(
      prevState => {
        const newStreak = isCorrect ? prevState.streak + 1 : 0;
        const totalCorrect = isCorrect
          ? prevState.totalCorrect + 1
          : prevState.totalCorrect;
        const updatedUserAnswers = [...prevState.userAnswers, userAnswer];

        return {
          selectedAnswer: answer,
          showResult: true,
          nextQuestionAvailable: true,
          answeringEnabled: false,
          userAnswers: updatedUserAnswers,
          totalCorrect,
          streak: newStreak,
        };
      },
      () => {
        // Auto-scroll to the explanation and next button
        setTimeout(() => {
          const resultElement = document.querySelector('.result-explanation');
          if (resultElement) {
            resultElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 300);

        if (isCorrect && this.state.streak % 3 === 0) {
          this.triggerSuccessEffects();
          this.showSnackbar(
            `¡Excelente! Has respondido ${this.state.streak} preguntas seguidas correctamente. ¡Sigue así!`
          );
        }
      }
    );

    try {
      // Use the JWT-based endpoint to answer the question
      await ApiStore.answerQuestionAuto(
        questionData.questionId,
        isCorrect,
        organizationName
      );
    } catch (error) {
      console.error('Error submitting answer:', error);
    }
  };

  handleNextQuestion = () => {
    this.setState(
      prevState => ({
        currentQuestionIndex: prevState.currentQuestionIndex + 1,
      }),
      () => {
        if (this.state.currentQuestionIndex >= this.state.questionIds.length) {
          this.setState({ completed: true });
        } else {
          this.loadNextQuestion();
        }
      }
    );
  };

  restartQuestions = () => {
    const { questionIds } = this.state;

    // If there are no questions, just keep the completed state
    if (!questionIds || questionIds.length === 0) {
      return;
    }

    this.setState(
      {
        completed: false,
        currentQuestionIndex: 0,
        totalCorrect: 0,
        userAnswers: [],
        streak: 0,
      },
      () => {
        this.shuffleQuestionIds();
        this.loadNextQuestion();
      }
    );
  };

  triggerSuccessEffects = () => {
    // Play sound
    const randomIndex = Math.floor(Math.random() * this.successSounds.length);
    const randomSound = this.successSounds[randomIndex];
    randomSound.load();
    randomSound.play().catch(error => {
      console.error('Error playing success sound:', error);
    });

    // Trigger confetti
    confetti({
      particleCount: 200,
      spread: 80,
      origin: { y: 0.95 },
    });
  };

  showSnackbar = message => {
    this.setState({ showSnackbar: true, snackbarMessage: message });
    setTimeout(() => {
      this.setState({ showSnackbar: false, snackbarMessage: '' });
    }, 6000);
  };

  renderCompletionSummary = () => {
    const { totalCorrect, questionIds, userAnswers } = this.state;
    const accuracy = ((totalCorrect / questionIds.length) * 100).toFixed(2);

    return (
      <div className="flex flex-col items-center justify-center h-full p-4">
        <h2 className="text-2xl font-bold mb-4">
          ¡Has completado todas las preguntas!
        </h2>
        <p className="mb-2">
          Respuestas correctas: {totalCorrect} de {questionIds.length}
        </p>
        <p className="mb-4">Precisión: {accuracy}%</p>

        {/* Questions Review */}
        <div className="w-full max-w-3xl mt-6">
          <h3 className="text-xl font-bold mb-4">Revisión de preguntas:</h3>
          {userAnswers.map((answer, index) => (
            <div key={index} className="mb-6 p-4 bg-white rounded-lg shadow-md">
              <h4 className="font-semibold mb-2">
                {index + 1}. {answer.questionText}
              </h4>
              {answer.image && answer.image !== noImage && (
                <div className="mb-2">
                  <img
                    src={answer.image}
                    alt="Question illustration"
                    className="w-full max-w-sm"
                  />
                </div>
              )}
              <p
                className={`mb-1 ${
                  answer.isCorrect ? 'text-green-600' : 'text-red-600'
                }`}
              >
                Tu respuesta: {answer.selectedAnswer}
              </p>
              {!answer.isCorrect && (
                <p className="mb-1 text-green-600">
                  Respuesta correcta: {answer.correctAnswer}
                </p>
              )}
              {answer.explanation && (
                <p className="text-gray-700 mt-2">{answer.explanation}</p>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={this.restartQuestions}
          className="mt-6 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full text-lg transition duration-300"
        >
          Empezar de nuevo
        </button>
      </div>
    );
  };

  render() {
    const {
      isLoading,
      questionData,
      selectedAnswer,
      showResult,
      answeringEnabled,
      nextQuestionAvailable,
      currentQuestionIndex,
      questionIds,
      completed,
      showSnackbar,
      snackbarMessage,
      streak,
    } = this.state;

    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <RingLoader color="#000000" size={80} />
        </div>
      );
    }

    if (completed) {
      return (
        <div className="bg-gray-100 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-sm p-6 max-w-3xl w-full">
            {questionIds.length > 0 ? (
              this.renderCompletionSummary()
            ) : (
              <div className="text-center py-8">
                <h2 className="text-2xl font-bold mb-4">
                  No hay preguntas disponibles para este video
                </h2>
                <p className="text-gray-600 mb-6">
                  No se han encontrado preguntas asociadas a este contenido.
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Return early if questionData is null to prevent errors
    if (!questionData) {
      return (
        <div className="bg-gray-100 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-sm p-6 max-w-3xl w-full text-center">
            <h2 className="text-2xl font-bold mb-4">
              No hay preguntas disponibles
            </h2>
            <p className="text-gray-600 mb-6">
              No se pudieron cargar las preguntas para este video.
            </p>
          </div>
        </div>
      );
    }

    const hasImage = questionData.image !== noImage;

    return (
      <div className="bg-gray-100 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <header className="bg-white rounded-lg shadow-sm p-4 mb-6 flex justify-between items-center">
            <div className="text-xl font-bold text-gray-800">
              Pregunta {currentQuestionIndex + 1} de {questionIds.length}
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                <span className="text-gray-600">{streak}</span>
              </div>
            </div>
          </header>

          {/* Main content */}
          <main className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex flex-col md:flex-row md:space-x-6">
              {/* Image frame */}
              <div className="md:w-1/3 mb-6 md:mb-0">
                <div className="aspect-w-1 aspect-h-1 bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={questionData.image}
                    alt={
                      hasImage ? 'Question illustration' : 'No image available'
                    }
                    className={`object-cover w-full h-full ${
                      hasImage ? '' : 'opacity-20'
                    }`}
                  />
                </div>
              </div>

              {/* Question and answers */}
              <div className="md:w-2/3">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">
                  {questionData.question}
                </h2>
                <div className="space-y-4 mb-6">
                  {questionData.answers.map((answer, index) => (
                    <button
                      key={index}
                      onClick={() => this.handleAnswerSelection(answer)}
                      disabled={!answeringEnabled}
                      className={`w-full py-3 px-4 rounded-md text-left transition-all duration-300 ${
                        showResult
                          ? selectedAnswer === answer
                            ? answer.isCorrect
                              ? 'bg-green-500 text-white'
                              : 'bg-red-500 text-white'
                            : 'bg-gray-100 text-gray-800'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                      }`}
                    >
                      {answer.text}
                    </button>
                  ))}
                </div>
                {showResult && (
                  <div className="mt-6 p-4 bg-gray-50 rounded-md result-explanation">
                    <p
                      className={`font-bold ${
                        selectedAnswer.isCorrect
                          ? 'text-green-500'
                          : 'text-red-500'
                      }`}
                    >
                      {selectedAnswer.isCorrect ? '¡Correcto!' : '¡Incorrecto!'}
                    </p>
                    {questionData.explanation && (
                      <p className="mt-2 text-gray-700">
                        {questionData.explanation}
                      </p>
                    )}
                  </div>
                )}
                {nextQuestionAvailable && (
                  <button
                    className="mt-6 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-md transition duration-300"
                    onClick={this.handleNextQuestion}
                  >
                    {currentQuestionIndex + 1 >= questionIds.length
                      ? 'Ver resultados'
                      : 'Siguiente pregunta'}
                  </button>
                )}
              </div>
            </div>
          </main>

          {/* Snackbar */}
          {showSnackbar && (
            <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-md shadow-lg z-50 text-sm font-semibold animate-bounce">
              {snackbarMessage}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default InfinitePracticeMini;
